import type { ReactNode } from "react"

import { Button } from "@/components/ui/button"
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import type { FieldErrors } from "@/hooks/use-record-form"

/**
 * The shell every create/edit form renders inside.
 *
 * There was a `BaseRecordForm` here before with **zero call sites** — it had
 * no Cancel button, so no dialog could use it, and every form hand-rolled the
 * same footer instead. This version is the footer those forms actually need.
 */
export function RecordForm({
  onSubmit,
  onCancel,
  isSubmitting,
  submitLabel = "Create",
  children,
}: {
  onSubmit: (event: React.FormEvent) => void
  onCancel?: () => void
  isSubmitting: boolean
  submitLabel?: string
  children: ReactNode
}) {
  return (
    // noValidate: the browser's own validation bubble is off-system chrome
    // and, worse, it blocks submission — so the server's message never arrives
    // and the inline error below the field never renders. Required-ness is
    // marked with an asterisk and enforced by the server.
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
      {children}
      <div className="flex justify-end gap-2 pt-1">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        )}
        <Button type="submit" size="sm" disabled={isSubmitting}>
          {isSubmitting ? "Saving…" : submitLabel}
        </Button>
      </div>
    </form>
  )
}

/** A titled group of fields. Long forms read as sections, not as one column. */
export function FormSection({
  title,
  children,
}: {
  title?: string
  children: ReactNode
}) {
  return (
    <section className="flex flex-col gap-3">
      {title && (
        <h3 className="text-muted-foreground text-xs font-semibold">
          {title}
        </h3>
      )}
      {children}
    </section>
  )
}

export function FormRow({ children }: { children: ReactNode }) {
  return <div className="grid gap-4 sm:grid-cols-2">{children}</div>
}

interface FieldProps {
  label: string
  name: string
  /**
   * Accepts both server shapes. Records return `{ name: ["can't be blank"] }`;
   * tasks and files return `{ title: "Title can't be blank" }`. Forms should
   * not have to know which one they are wired to.
   */
  errors?: FieldErrors | Record<string, string | undefined>
  required?: boolean
  /** Marks the label "(optional)" in muted text, the counterpart of the asterisk. */
  optional?: boolean
  hint?: ReactNode
  children?: ReactNode
}

function messageFor(errors: FieldProps["errors"], name: string) {
  const value = errors?.[name]
  if (!value) return null
  return Array.isArray(value) ? value[0] : value
}

/**
 * Label + control + inline error, in one place — the stock Field family at
 * stock sizes. Labels were text-xs here while every other Label rendered
 * text-sm, so the same word was two sizes depending on the surface.
 *
 * The error markup used to be copy-pasted into eight-plus components, which is
 * why `border-destructive` was applied in some and forgotten in others.
 */
export function FormControl({ label, name, errors, required, optional, hint, children }: FieldProps) {
  const message = messageFor(errors, name)
  return (
    <Field data-invalid={message ? true : undefined}>
      <FieldLabel htmlFor={name}>
        {label}
        {required && <span className="text-muted-foreground ml-0.5">*</span>}
        {optional && <span className="text-muted-foreground font-normal">(optional)</span>}
      </FieldLabel>
      {children}
      {hint && !message && <FieldDescription>{hint}</FieldDescription>}
      {message && <FieldError>{message}</FieldError>}
    </Field>
  )
}

/** A plain text / email / number input, wired to the shared error display. */
export function FormField({
  label,
  name,
  type = "text",
  required,
  optional,
  placeholder,
  value,
  onChange,
  errors,
  hint,
  autoFocus,
}: FieldProps & {
  type?: string
  placeholder?: string
  value: string
  onChange: (value: string) => void
  autoFocus?: boolean
}) {
  const message = messageFor(errors, name)
  return (
    <FormControl label={label} name={name} errors={errors} required={required} optional={optional} hint={hint}>
      <Input
        id={name}
        name={name}
        type={type}
        placeholder={placeholder}
        value={value}
        autoFocus={autoFocus}
        // Stock Input styles aria-invalid itself; no manual border class.
        aria-invalid={Boolean(message)}
        onChange={(event) => onChange(event.target.value)}
      />
    </FormControl>
  )
}

export function FormTextarea({
  label,
  name,
  required,
  optional,
  placeholder,
  value,
  onChange,
  errors,
  hint,
  rows = 3,
}: FieldProps & {
  placeholder?: string
  value: string
  onChange: (value: string) => void
  rows?: number
}) {
  const message = messageFor(errors, name)
  return (
    <FormControl label={label} name={name} errors={errors} required={required} optional={optional} hint={hint}>
      <Textarea
        id={name}
        name={name}
        rows={rows}
        placeholder={placeholder}
        value={value}
        aria-invalid={Boolean(message)}
        onChange={(event) => onChange(event.target.value)}
      />
    </FormControl>
  )
}
